import React, { useState, useMemo, useRef } from 'react';
import styled from 'styled-components';
import {
  FiCheckCircle,
  FiAlertTriangle,
  FiFileText,
  FiTrendingUp,
  FiSearch,
  FiDownload,
  FiExternalLink,
  FiFilter,
  FiLayers,
  FiRefreshCw,
  FiHelpCircle,
  FiDownloadCloud,
} from 'react-icons/fi';
import * as XLSX from 'xlsx';
import {
  useConferenciaOptions,
  useConferenciaReport,
  getConferenciaReport,
  ConferenciaItem,
} from '../../../services/conferencia.service';
import Pagination from '../../../components/Table/Pagination';
import { LoaderComponent } from '../../../components/Table/styles';
import SyncTceModal, { SyncTceModalHandle } from './components/SyncTceModal';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  padding: 14px 18px;
  background-color: #f8fafc;
  box-sizing: border-box;
  gap: 12px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const TitleArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  h1 {
    font-size: 18px;
    font-weight: 800;
    color: #0f172a;
    margin: 0;
  }

  p {
    font-size: 12px;
    color: #64748b;
    margin: 0;
  }
`;

const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: #ffffff;
  color: #0f172a;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) {
    background-color: #f1f5f9;
    border-color: #94a3b8;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    font-size: 14px;
    color: #2563eb;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const SyncTceButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: #0284c7;
  color: #ffffff;
  border: 1px solid #0369a1;
  border-radius: 6px;
  padding: 6px 13px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(2, 132, 199, 0.2);

  &:hover:not(:disabled) {
    background-color: #0369a1;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    font-size: 14px;
    color: #ffffff;
  }
`;

const ExportButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: ${({ variant }) => (variant === 'primary' ? '#2563eb' : '#ffffff')};
  color: ${({ variant }) => (variant === 'primary' ? '#ffffff' : '#0f172a')};
  border: 1px solid ${({ variant }) => (variant === 'primary' ? '#1d4ed8' : '#cbd5e1')};
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) {
    background-color: ${({ variant }) => (variant === 'primary' ? '#1d4ed8' : '#f1f5f9')};
    border-color: ${({ variant }) => (variant === 'primary' ? '#1e40af' : '#94a3b8')};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    font-size: 14px;
    color: ${({ variant }) => (variant === 'primary' ? '#ffffff' : '#2563eb')};
  }
`;

const FilterCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 10px;
  align-items: flex-end;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #475569;
  }

  select,
  input {
    height: 32px;
    padding: 0 8px;
    border-radius: 6px;
    border: 1px solid #cbd5e1;
    background-color: #ffffff;
    font-size: 12px;
    color: #0f172a;
    font-weight: 500;
    outline: none;
    transition: border-color 0.2s ease;

    &:focus {
      border-color: #2563eb;
      box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
    }
  }
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
`;

const KpiCard = styled.div<{ borderColor: string; bgAccent: string }>`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-left: 3px solid ${({ borderColor }) => borderColor};
  border-radius: 8px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  position: relative;
  overflow: hidden;

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #64748b;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-bottom: 4px;

    svg {
      font-size: 16px;
      color: ${({ borderColor }) => borderColor};
    }
  }

  .value {
    font-size: 20px;
    font-weight: 800;
    color: #0f172a;
    line-height: 1.1;
  }

  .sub {
    font-size: 11px;
    color: #64748b;
    margin-top: 3px;
  }
`;

const ProgressBarWrapper = styled.div`
  width: 100%;
  height: 6px;
  background-color: #e2e8f0;
  border-radius: 3px;
  margin-top: 6px;
  overflow: hidden;
`;

const ProgressBar = styled.div<{ percent: number }>`
  width: ${({ percent }) => Math.min(100, Math.max(0, percent))}%;
  height: 100%;
  background: linear-gradient(90deg, #ef4444 0%, #f97316 35%, #eab308 65%, #10b981 100%);
  border-radius: 3px;
  transition: width 0.4s ease;
`;

const TableCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  position: relative;
`;

const TopProgressBar = styled.div<{ active: boolean }>`
  height: 3px;
  width: 100%;
  background: transparent;
  position: relative;
  overflow: hidden;
  opacity: ${({ active }) => (active ? 1 : 0)};
  transition: opacity 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 45%;
    background: linear-gradient(90deg, #3b82f6, #10b981, #3b82f6);
    border-radius: 2px;
    animation: indeterminate 1.1s infinite ease-in-out;
  }

  @keyframes indeterminate {
    0% {
      left: -45%;
    }
    100% {
      left: 100%;
    }
  }
`;

const TableOverlay = styled.div`
  position: absolute;
  top: 48px;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(1.5px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 15;
  gap: 12px;

  .loading-card {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #ffffff;
    padding: 10px 20px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border: 1px solid #e2e8f0;
    font-size: 13px;
    font-weight: 700;
    color: #1e293b;
  }
`;

const TableToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 14px;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 8px;
  background-color: #f8fafc;
`;

const TabsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  overflow-x: auto;
`;

const TabItem = styled.button<{ active: boolean; variant?: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid
    ${({ active, variant }) =>
      active
        ? variant === 'pendente'
          ? '#ef4444'
          : variant === 'digitalizado'
          ? '#10b981'
          : variant === 'nao_sim'
          ? '#d97706'
          : '#2563eb'
        : '#cbd5e1'};
  background-color: ${({ active, variant }) =>
    active
      ? variant === 'pendente'
        ? '#ef4444'
        : variant === 'digitalizado'
        ? '#10b981'
        : variant === 'nao_sim'
        ? '#d97706'
        : '#2563eb'
      : '#ffffff'};
  color: ${({ active }) => (active ? '#ffffff' : '#334155')};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ active, variant }) =>
      active
        ? variant === 'pendente'
          ? '#ef4444'
          : variant === 'digitalizado'
          ? '#10b981'
          : '#2563eb'
        : '#f1f5f9'};
  }
`;

const TabBadge = styled.span<{ active: boolean }>`
  font-size: 10px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 8px;
  background-color: ${({ active }) => (active ? 'rgba(255, 255, 255, 0.25)' : '#e2e8f0')};
  color: ${({ active }) => (active ? '#ffffff' : '#475569')};
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 4px 10px;
  width: 240px;
  height: 30px;
  max-width: 100%;

  svg {
    color: #64748b;
    font-size: 14px;
  }

  input {
    border: none;
    background: transparent;
    outline: none;
    width: 100%;
    font-size: 12px;
    color: #0f172a;

    &::placeholder {
      color: #94a3b8;
    }
  }
`;

const TableResponsive = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  white-space: nowrap;
  font-size: 12px;

  th {
    padding: 8px 10px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #1e293b;
    text-align: left;
    background-color: #f8fafc;
    border-bottom: 2px solid #cbd5e1;
  }

  td {
    padding: 7px 10px;
    color: #1e293b;
    border-bottom: 1px solid #e2e8f0;
    max-width: 280px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  tbody tr {
    transition: background-color 0.15s ease-in-out;
    background-color: #ffffff;

    &:nth-child(even) {
      background-color: #f8fafc;
    }

    &:hover {
      background-color: #f1f5f9;
    }
  }
`;

const StatusBadge = styled.span<{ status: 'DIGITALIZADO' | 'PENDENTE' | 'NAO_SIM' }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 7px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;

  ${({ status }) =>
    status === 'DIGITALIZADO'
      ? `
    background-color: #d1fae5;
    color: #065f46;
    border: 1px solid #a7f3d0;
  `
      : status === 'NAO_SIM'
      ? `
    background-color: #fef3c7;
    color: #92400e;
    border: 1px solid #fde68a;
  `
      : `
    background-color: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
  `}
`;

const ActionLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background-color: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background-color: #dbeafe;
    border-color: #93c5fd;
  }
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #64748b;
  font-size: 14px;
  gap: 12px;

  svg {
    font-size: 40px;
    color: #94a3b8;
  }
`;

const formatCurrency = (val: any) => {
  if (val === null || val === undefined || val === '') return '-';
  const num = typeof val === 'number' ? val : parseFloat(String(val).replace(',', '.'));
  if (isNaN(num)) return String(val);
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
};

const formatDate = (val: any) => {
  if (!val) return '-';
  if (typeof val === 'string' && val.includes('/')) return val;
  const str = String(val);
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  return str;
};

const getTaxaColor = (percent: number) => {
  if (percent >= 80) return '#10b981'; // Verde
  if (percent >= 60) return '#eab308'; // Amarelo
  if (percent >= 40) return '#f97316'; // Laranja
  return '#ef4444'; // Vermelho
};

const getTaxaBg = (percent: number) => {
  if (percent >= 80) return '#f0fdf4';
  if (percent >= 60) return '#fefce8';
  if (percent >= 40) return '#fff7ed';
  return '#fef2f2';
};

const Conferencia: React.FC = () => {
  // Opções de filtro do backend
  const { data: options, isLoading: loadingOptions } = useConferenciaOptions();

  // Estados dos filtros
  const [selectedMunicipio, setSelectedMunicipio] = useState<string>('');
  const [selectedTipo, setSelectedTipo] = useState<'cont' | 'lic'>('cont');
  const [selectedAno, setSelectedAno] = useState<string>('');
  const [selectedMes, setSelectedMes] = useState<string>('ALL');
  const [selectedSecretaria, setSelectedSecretaria] = useState<string>('ALL');
  const [activeTab, setActiveTab] = useState<'pendentes' | 'digitalizados' | 'nao_localizados_sim' | 'all'>('digitalizados');
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);

  // Inicialização inteligente do município e ano selecionados
  React.useEffect(() => {
    if (options && options.municipios.length > 0 && !selectedMunicipio) {
      const firstMun = options.municipios[0];
      setSelectedMunicipio(firstMun.nome);
      if (firstMun.anos.length > 0) {
        setSelectedAno(firstMun.anos[0]);
      }
    }
  }, [options, selectedMunicipio]);

  // Atualizar anos ao trocar de município
  const anosDisponiveis = useMemo(() => {
    if (!options || !selectedMunicipio) return ['2026', '2025', '2024'];
    const mun = options.municipios.find(
      (m) => m.nome.toLowerCase() === selectedMunicipio.toLowerCase()
    );
    return mun && mun.anos.length > 0 ? mun.anos : ['2026', '2025', '2024'];
  }, [options, selectedMunicipio]);

  // Se o ano atual não estiver na lista de anos do município selecionado, ajusta automaticamente
  React.useEffect(() => {
    if (anosDisponiveis.length > 0 && (!selectedAno || !anosDisponiveis.includes(selectedAno))) {
      setSelectedAno(anosDisponiveis[0]);
    }
  }, [anosDisponiveis, selectedAno]);

  // Resetar secretaria e paginação ao trocar município ou ano
  React.useEffect(() => {
    setSelectedSecretaria('ALL');
    setPage(1);
  }, [selectedMunicipio, selectedAno]);

  // Consulta do Relatório de Conferência
  const {
    data: report,
    isLoading: loadingReport,
    isFetching,
    refetch,
  } = useConferenciaReport(
    {
      municipio: selectedMunicipio,
      tipoProcesso: selectedTipo,
      ano: selectedAno,
      mes: selectedMes,
      secretaria: selectedSecretaria,
      status: activeTab,
      search,
      page,
      limit: 50,
    },
    !!selectedMunicipio && !!selectedAno
  );

  // Se o relatório carregar e não houver digitalizados mas houver pendentes, alternar a aba para 'pendentes'
  React.useEffect(() => {
    if (report && !loadingReport && !isFetching) {
      if (
        activeTab === 'digitalizados' &&
        report.kpis.totalDigitalizados === 0 &&
        report.kpis.totalPendentes > 0
      ) {
        setActiveTab('pendentes');
      }
    }
  }, [report, loadingReport, isFetching, activeTab]);

  // Lista de Secretarias instantânea a partir do relatório da conferência
  const secretariasDisponiveis = useMemo(() => {
    return report?.secretariasDisponiveis || [];
  }, [report?.secretariasDisponiveis]);

  const kpis = report?.kpis || {
    totalSim: 0,
    totalDigitalizados: 0,
    totalPendentes: 0,
    totalNaoLocalizadosSim: 0,
    totalTodos: 0,
    taxaDigitalizacao: 0,
  };

  const [exportingType, setExportingType] = useState<'completo' | 'conferencia' | null>(null);
  const syncModalRef = useRef<SyncTceModalHandle>(null);

  const handleOpenSyncModal = () => {
    syncModalRef.current?.openSyncModal({
      municipio: selectedMunicipio,
      ano: selectedAno,
      mes: selectedMes,
      tipoProcesso: selectedTipo,
    });
  };

  const handleSyncSuccess = () => {
    refetch();
  };

  // Exportar dados para XLSX (Excel) em 2 modelos: Completo (table-procImp) ou Conferência (table-procConf)
  const handleExportExcel = async (modelo: 'completo' | 'conferencia') => {
    if (!report || report.total === 0) return;

    try {
      setExportingType(modelo);

      // Buscar todos os registros do filtro atual (sem limite de paginação)
      let exportItems: ConferenciaItem[] = report.data;
      if (report.total > report.data.length) {
        const fullReport = await getConferenciaReport({
          municipio: selectedMunicipio,
          tipoProcesso: selectedTipo,
          ano: selectedAno,
          mes: selectedMes,
          secretaria: selectedSecretaria,
          status: activeTab,
          search,
          page: 1,
          limit: 100000,
        });
        if (fullReport && fullReport.data && fullReport.data.length > 0) {
          exportItems = fullReport.data;
        }
      }

      let headers: string[] = [];
      let rows: any[][] = [];
      let colWidths: { wch: number }[] = [];
      let sheetName = 'Conferência GED';

      if (modelo === 'conferencia') {
        // Modelo 2: Gerar Conferência (table-procConf)
        if (selectedTipo === 'cont') {
          headers = ['Número Documento de Caixa', 'Secretaria', 'Valor', 'Credor'];
          colWidths = [{ wch: 24 }, { wch: 38 }, { wch: 18 }, { wch: 38 }];
          rows = exportItems.map((item) => {
            const valorNum =
              typeof item.valor === 'number'
                ? item.valor
                : item.valor
                ? parseFloat(String(item.valor).replace(',', '.'))
                : 0;

            return [
              item.identificador || 'S/N',
              item.secretaria || '-',
              isNaN(valorNum) ? item.valor || 0 : valorNum,
              item.credor || '-',
            ];
          });
        } else {
          headers = [
            'Número da Licitação / Processo',
            'Data',
            'Credor / Vencedor',
            'Nº Empenho',
            'Valor',
            'Objeto',
          ];
          colWidths = [{ wch: 24 }, { wch: 14 }, { wch: 38 }, { wch: 16 }, { wch: 18 }, { wch: 55 }];
          rows = exportItems.map((item) => {
            const valorNum =
              typeof item.valor === 'number'
                ? item.valor
                : item.valor
                ? parseFloat(String(item.valor).replace(',', '.'))
                : 0;

            return [
              item.identificador || 'S/N',
              formatDate(item.data),
              item.credor || '-',
              item.empenho || '-',
              isNaN(valorNum) ? item.valor || 0 : valorNum,
              item.objeto || '-',
            ];
          });
        }
        sheetName = 'Conferência SIM';
      } else {
        // Modelo 1: Gerar Excel Completo (table-procImp)
        headers = [
          'ID Documento GED',
          selectedTipo === 'cont' ? 'Doc. Caixa' : 'Nº Processo / Licitação',
          'Data',
          selectedTipo === 'cont' ? 'Credor / Favorecido' : 'Vencedor / Fornecedor',
          'Nº Empenho',
          'Valor',
          'Objeto',
          'Secretaria',
          'Páginas',
          'Status GED',
        ];

        colWidths = [
          { wch: 28 }, // ID GED
          { wch: 18 }, // Identificador
          { wch: 14 }, // Data
          { wch: 38 }, // Credor
          { wch: 14 }, // Empenho
          { wch: 18 }, // Valor
          { wch: 55 }, // Objeto
          { wch: 38 }, // Secretaria
          { wch: 12 }, // Páginas
          { wch: 16 }, // Status
        ];

        rows = exportItems.map((item) => {
          const valorNum =
            typeof item.valor === 'number'
              ? item.valor
              : item.valor
              ? parseFloat(String(item.valor).replace(',', '.'))
              : 0;

          return [
            item.gedDocument ? item.gedDocument._id : 'NÃO LOCALIZADO',
            item.identificador || 'S/N',
            formatDate(item.data),
            item.credor || '-',
            item.empenho || '-',
            isNaN(valorNum) ? item.valor || 0 : valorNum,
            item.objeto || '-',
            item.secretaria || '-',
            item.gedDocument?.pages || (item.status === 'DIGITALIZADO' ? 1 : '-'),
            item.status === 'DIGITALIZADO' ? 'DIGITALIZADO' : 'PENDENTE',
          ];
        });
        sheetName = 'Relatório Geral';
      }

      // Criar a planilha XLSX
      const worksheetData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      worksheet['!cols'] = colWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      const prefix = modelo === 'conferencia' ? 'Gerar_Conferencia' : 'Gerar_Excel';
      const fileName = `${prefix}_${selectedMunicipio}_${selectedTipo}_${selectedAno}_${selectedMes}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (err) {
      console.error(`Erro ao gerar planilha Excel (${modelo}):`, err);
    } finally {
      setExportingType(null);
    }
  };

  return (
    <Container>
      <PageHeader>
        <TitleArea>
          <h1>Conferência de Documentos (SIM / TCE-CE vs GED)</h1>
          <p>
            Cruze em tempo real os atos contábeis e licitatórios do Tribunal com os documentos digitalizados no sistema.
          </p>
        </TitleArea>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <SyncTceButton
            type="button"
            onClick={handleOpenSyncModal}
            title="Importar e sincronizar dados oficiais diretamente da API do TCE-CE (SIM)"
          >
            <FiDownloadCloud />
            <span>Sincronizar TCE</span>
          </SyncTceButton>

          <RefreshButton
            type="button"
            onClick={() => refetch()}
            disabled={isFetching || !!exportingType}
            title="Atualizar dados da conferência"
          >
            <FiRefreshCw className={isFetching ? 'spin' : ''} />
            <span>{isFetching ? 'Atualizando...' : 'Atualizar Dados'}</span>
          </RefreshButton>

          <ExportButton
            type="button"
            onClick={() => handleExportExcel('completo')}
            disabled={!report?.data?.length || isFetching || !!exportingType}
            title="Exportar modelo completo com todos os dados e páginas (table-procImp)"
          >
            {exportingType === 'completo' ? <FiRefreshCw className="spin" /> : <FiDownload />}
            <span>{exportingType === 'completo' ? 'Exportando...' : 'Gerar Excel'}</span>
          </ExportButton>

          <ExportButton
            type="button"
            variant="primary"
            onClick={() => handleExportExcel('conferencia')}
            disabled={!report?.data?.length || isFetching || !!exportingType}
            title="Exportar modelo enxuto de conciliação e conferência (table-procConf)"
          >
            {exportingType === 'conferencia' ? <FiRefreshCw className="spin" /> : <FiFileText />}
            <span>{exportingType === 'conferencia' ? 'Exportando...' : 'Gerar Conferência'}</span>
          </ExportButton>
        </div>
      </PageHeader>

      {/* Painel de Filtros */}
      <FilterCard>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0f172a', fontWeight: 700, fontSize: 14 }}>
          <FiFilter style={{ color: '#2563eb' }} />
          <span>Parâmetros de Auditoria</span>
        </div>

        <FilterGrid>
          <FormGroup>
            <label>Município</label>
            <select
              value={selectedMunicipio}
              onChange={(e) => {
                const newMun = e.target.value;
                setSelectedMunicipio(newMun);
                setPage(1);
                const munObj = options?.municipios.find(
                  (m) => m.nome.toLowerCase() === newMun.toLowerCase()
                );
                if (munObj && munObj.anos.length > 0) {
                  if (!munObj.anos.includes(selectedAno)) {
                    setSelectedAno(munObj.anos[0]);
                  }
                }
              }}
            >
              {options?.municipios.map((m) => (
                <option key={m.nome} value={m.nome}>
                  {m.nome} {m.codigo ? `(${m.codigo})` : ''}
                </option>
              ))}
            </select>
          </FormGroup>

          <FormGroup>
            <label>Tipo de Processo</label>
            <select
              value={selectedTipo}
              onChange={(e) => {
                setSelectedTipo(e.target.value as 'cont' | 'lic');
                setPage(1);
              }}
            >
              <option value="cont">Processo Contábil (Doc. Caixa / Pagamentos)</option>
              <option value="lic">Processo Licitatório (Licitações)</option>
            </select>
          </FormGroup>

          <FormGroup>
            <label>Exercício / Ano</label>
            <select
              value={selectedAno}
              onChange={(e) => {
                setSelectedAno(e.target.value);
                setPage(1);
              }}
            >
              {anosDisponiveis.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </FormGroup>

          <FormGroup>
            <label>Mês de Referência</label>
            <select
              value={selectedMes}
              onChange={(e) => {
                setSelectedMes(e.target.value);
                setPage(1);
              }}
            >
              {options?.meses.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </FormGroup>

          {selectedTipo === 'cont' && (
            <FormGroup>
              <label>Secretaria / Unidade Gestora</label>
              <select
                value={selectedSecretaria}
                onChange={(e) => {
                  setSelectedSecretaria(e.target.value);
                  setPage(1);
                }}
              >
                <option value="ALL">Todas as Secretarias / UGs</option>
                {secretariasDisponiveis.map((sec) => (
                  <option key={sec} value={sec}>
                    {sec}
                  </option>
                ))}
              </select>
            </FormGroup>
          )}
        </FilterGrid>
      </FilterCard>

      {/* Cartões de Indicadores (KPIs) */}
      <KpiGrid>
        <KpiCard borderColor="#2563eb" bgAccent="#eff6ff">
          <div className="header">
            <span>Total no Tribunal (SIM)</span>
            <FiLayers />
          </div>
          <div className="value">{kpis.totalSim}</div>
          <div className="sub" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
            <span>{kpis.totalSim > 0 ? 'Registros cadastrados no TCE-CE' : 'Sem carga do SIM para o ano'}</span>
            {kpis.totalSim === 0 && (
              <button
                type="button"
                onClick={handleOpenSyncModal}
                style={{
                  border: 'none',
                  background: '#2563eb',
                  color: '#ffffff',
                  fontSize: '11px',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  flexShrink: 0,
                }}
                title="Sincronizar dados do TCE-CE agora"
              >
                <FiDownloadCloud size={12} /> Sincronizar
              </button>
            )}
          </div>
        </KpiCard>

        <KpiCard borderColor="#ef4444" bgAccent="#fef2f2">
          <div className="header">
            <span>Faltam Digitalizar (Pendentes)</span>
            <FiAlertTriangle />
          </div>
          <div className="value" style={{ color: '#dc2626' }}>
            {kpis.totalPendentes}
          </div>
          <div className="sub">Documentos ainda não indexados no GED</div>
        </KpiCard>

        <KpiCard borderColor="#10b981" bgAccent="#f0fdf4">
          <div className="header">
            <span>Digitalizados no GED</span>
            <FiCheckCircle />
          </div>
          <div className="value" style={{ color: '#059669' }}>
            {kpis.totalDigitalizados}
          </div>
          <div className="sub">
            {kpis.totalNaoLocalizadosSim
              ? `${kpis.totalDigitalizados} arquivados (${kpis.totalNaoLocalizadosSim} fora do SIM)`
              : 'Arquivados e localizados no GED'}
          </div>
        </KpiCard>

        <KpiCard
          borderColor={getTaxaColor(kpis.taxaDigitalizacao)}
          bgAccent={getTaxaBg(kpis.taxaDigitalizacao)}
        >
          <div className="header">
            <span>Taxa de Cobertura</span>
            <FiTrendingUp style={{ color: getTaxaColor(kpis.taxaDigitalizacao) }} />
          </div>
          <div className="value" style={{ color: getTaxaColor(kpis.taxaDigitalizacao) }}>
            {kpis.taxaDigitalizacao}%
          </div>
          <ProgressBarWrapper>
            <ProgressBar percent={kpis.taxaDigitalizacao} />
          </ProgressBarWrapper>
        </KpiCard>
      </KpiGrid>

      {/* Tabela de Resultados com Abas */}
      <TableCard>
        <TopProgressBar active={isFetching} />
        {isFetching && report && (
          <TableOverlay>
            <div className="loading-card">
              <FiRefreshCw className="spin" style={{ color: '#2563eb', fontSize: 16 }} />
              <span>Atualizando conferência...</span>
            </div>
          </TableOverlay>
        )}
        <TableToolbar>
          <TabsWrapper>
            <TabItem
              active={activeTab === 'digitalizados'}
              variant="digitalizado"
              onClick={() => {
                setActiveTab('digitalizados');
                setPage(1);
              }}
              type="button"
            >
              <FiCheckCircle />
              <span>Digitalizados</span>
              <TabBadge active={activeTab === 'digitalizados'}>{kpis.totalDigitalizados}</TabBadge>
            </TabItem>

            <TabItem
              active={activeTab === 'pendentes'}
              variant="pendente"
              onClick={() => {
                setActiveTab('pendentes');
                setPage(1);
              }}
              type="button"
            >
              <FiAlertTriangle />
              <span>Falta Digitar</span>
              <TabBadge active={activeTab === 'pendentes'}>{kpis.totalPendentes}</TabBadge>
            </TabItem>

            <TabItem
              active={activeTab === 'nao_localizados_sim'}
              variant="nao_sim"
              onClick={() => {
                setActiveTab('nao_localizados_sim');
                setPage(1);
              }}
              type="button"
            >
              <FiHelpCircle />
              <span>Não no SIM</span>
              <TabBadge active={activeTab === 'nao_localizados_sim'}>
                {kpis.totalNaoLocalizadosSim || 0}
              </TabBadge>
            </TabItem>

            <TabItem
              active={activeTab === 'all'}
              onClick={() => {
                setActiveTab('all');
                setPage(1);
              }}
              type="button"
            >
              <FiLayers />
              <span>Todos os Documentos</span>
              <TabBadge active={activeTab === 'all'}>{kpis.totalTodos ?? report?.total ?? 0}</TabBadge>
            </TabItem>
          </TabsWrapper>

          <SearchBox>
            <FiSearch />
            <input
              type="text"
              placeholder="Buscar por número, credor, objeto..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </SearchBox>
        </TableToolbar>

        <TableResponsive>
          {loadingReport ? (
            <div style={{ padding: 60, display: 'flex', justifyContent: 'center' }}>
              <LoaderComponent />
            </div>
          ) : !report || report.data.length === 0 ? (
            <EmptyContainer>
              <FiFileText />
              <span>Nenhum documento encontrado para os filtros selecionados.</span>
            </EmptyContainer>
          ) : (
            <StyledTable>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>{selectedTipo === 'cont' ? 'Doc. Caixa' : 'Nº Processo / Licitação'}</th>
                  <th>Data</th>
                  <th>{selectedTipo === 'cont' ? 'Credor / Favorecido' : 'Vencedor / Fornecedor'}</th>
                  <th>Valor</th>
                  {selectedTipo === 'cont' && <th>Nº Empenho</th>}
                  <th>{selectedTipo === 'cont' ? 'Secretaria / UG' : 'Modalidade'}</th>
                  <th>Objeto</th>
                  <th style={{ textAlign: 'center' }}>Ação no GED</th>
                </tr>
              </thead>
              <tbody>
                {report.data.map((item: ConferenciaItem) => (
                  <tr key={item._id}>
                    <td>
                      <StatusBadge
                        status={
                          item.naoLocalizadoSim
                            ? 'NAO_SIM'
                            : item.status === 'DIGITALIZADO'
                            ? 'DIGITALIZADO'
                            : 'PENDENTE'
                        }
                      >
                        {item.naoLocalizadoSim ? (
                          <FiHelpCircle />
                        ) : item.status === 'DIGITALIZADO' ? (
                          <FiCheckCircle />
                        ) : (
                          <FiAlertTriangle />
                        )}
                        <span>
                          {item.naoLocalizadoSim
                            ? 'Não no SIM'
                            : item.status === 'DIGITALIZADO'
                            ? 'Digitalizado'
                            : 'Pendente'}
                        </span>
                      </StatusBadge>
                    </td>
                    <td>
                      <strong>{item.identificador}</strong>
                    </td>
                    <td>{formatDate(item.data)}</td>
                    <td>{item.credor || '-'}</td>
                    <td style={{ fontWeight: 600 }}>{formatCurrency(item.valor)}</td>
                    {selectedTipo === 'cont' && (
                      <td>
                        {item.empenho ? (
                          <span>
                            {item.empenho}
                            {item.anoEmpenho ? `/${item.anoEmpenho}` : ''}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                    )}
                    <td>{item.secretaria || '-'}</td>
                    <td title={item.objeto}>{item.objeto || '-'}</td>
                    <td style={{ textAlign: 'center' }}>
                      {item.gedDocument ? (
                        <ActionLink
                          href={`/document/${item.gedDocument._id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Abrir no GED"
                        >
                          <span>Ver PDF</span>
                          <FiExternalLink />
                        </ActionLink>
                      ) : (
                        <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
                          Não localizado
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          )}
        </TableResponsive>

        {report && report.totalPages > 1 && (
          <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0' }}>
            <Pagination
              totalPages={report.totalPages}
              page={page}
              perPage={report.limit}
              pageChangeHandler={setPage}
            />
          </div>
        )}
      </TableCard>

      {/* Modal de Sincronização direta com a API do TCE-CE */}
      <SyncTceModal
        ref={syncModalRef}
        municipios={options?.municipios || []}
        onSuccess={handleSyncSuccess}
      />
    </Container>
  );
};

export default Conferencia;
